import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Button, Spinner, Modal, Form, Tabs, Tab } from 'react-bootstrap';
import { useAuth } from '../../../../contexts/AuthContext';
import { FaEdit, FaPlus, FaWater, FaTrophy } from 'react-icons/fa';

export default function AdminLakeTournaments() {
  const { token } = useAuth();
  const [lakes, setLakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Modals
  const [showLakeModal, setShowLakeModal] = useState(false);
  const [showTourneyModal, setShowTourneyModal] = useState(false);
  
  // Lake Form State
  const [lakeId, setLakeId] = useState('');
  const [lakeName, setLakeName] = useState('');
  const [lakeState, setLakeState] = useState('');
  const [lakeDescription, setLakeDescription] = useState('');
  const [lakeHero, setLakeHero] = useState('');
  
  // Tournament Form State
  const [tourneyId, setTourneyId] = useState('');
  const [tourneyLakeId, setTourneyLakeId] = useState('');
  const [tourneyTitle, setTourneyTitle] = useState('');
  const [tourneyOrganizer, setTourneyOrganizer] = useState('');
  const [tourneyStart, setTourneyStart] = useState('');
  const [tourneyEnd, setTourneyEnd] = useState('');
  const [tourneyRegUrl, setTourneyRegUrl] = useState('');

  useEffect(() => {
    fetchLakes();
  }, [token]);

  const fetchLakes = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/lakes`);
      const data = await res.json();
      if (data.success) {
        setLakes(data.lakes || []);
      }
    } catch (error) {
      console.error("Error fetching lakes", error);
    } finally {
      setLoading(false);
    }
  };

  const openLakeModal = (lake = null) => {
    if (lake) {
      setLakeId(lake.id);
      setLakeName(lake.name);
      setLakeState(lake.state || '');
      setLakeDescription(lake.description || '');
      setLakeHero(lake.heroImage || '');
    } else {
      setLakeId('');
      setLakeName('');
      setLakeState('');
      setLakeDescription('');
      setLakeHero('');
    }
    setShowLakeModal(true);
  };

  const handleLakeSubmit = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/admin/lakes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          id: lakeId, 
          name: lakeName, 
          state: lakeState, 
          description: lakeDescription,
          heroImage: lakeHero
        })
      });
      const data = await res.json();
      if (data.success) {
        fetchLakes();
        setShowLakeModal(false);
      } else {
        alert(data.error || "Failed to save lake");
      }
    } catch (error) {
      console.error("Error saving lake", error);
    } finally {
      setActionLoading(false);
    }
  };

  const openTourneyModal = () => {
    setTourneyId('');
    setTourneyLakeId(lakes[0]?.id || '');
    setTourneyTitle('');
    setTourneyOrganizer('');
    setTourneyStart('');
    setTourneyEnd('');
    setTourneyRegUrl('');
    setShowTourneyModal(true);
  };

  const handleTourneySubmit = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/admin/tournaments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          id: tourneyId, 
          lakeId: tourneyLakeId, 
          title: tourneyTitle,
          organizer: tourneyOrganizer,
          startDate: tourneyStart,
          endDate: tourneyEnd,
          registrationUrl: tourneyRegUrl
        })
      });
      const data = await res.json();
      if (data.success) {
        // Just alert for now since we don't have a direct tournament list in this simple view
        alert("Tournament saved successfully!");
        setShowTourneyModal(false);
      } else {
        alert(data.error || "Failed to save tournament");
      }
    } catch (error) {
      console.error("Error saving tournament", error);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  return (
    <div className="py-4 community-admin-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Lake & Tournament Manager</h2>
        <div>
          <Button variant="outline-primary" className="me-2" onClick={() => openLakeModal()}>
            <FaPlus className="me-2" /> Add Lake
          </Button>
          <Button variant="primary" onClick={() => openTourneyModal()} disabled={lakes.length === 0}>
            <FaTrophy className="me-2" /> Add Tournament
          </Button>
        </div>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th>Lake Name</th>
                <th>State</th>
                <th>Slug</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lakes.length > 0 ? (
                lakes.map(lake => (
                  <tr key={lake.id}>
                    <td className="fw-bold"><FaWater className="text-info me-2"/> {lake.name}</td>
                    <td>{lake.state}</td>
                    <td><code>{lake.slug}</code></td>
                    <td className="text-end">
                      <Button variant="outline-primary" size="sm" onClick={() => openLakeModal(lake)}>
                        <FaEdit /> Edit
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-4 text-muted">
                    No lakes found. Add a lake to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Add Lake Modal */}
      <Modal show={showLakeModal} onHide={() => setShowLakeModal(false)}>
        <Form onSubmit={handleLakeSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{lakeId ? 'Edit Lake' : 'Add New Lake'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Lake Name <span className="text-danger">*</span></Form.Label>
              <Form.Control type="text" value={lakeName} onChange={e => setLakeName(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>State</Form.Label>
              <Form.Control type="text" value={lakeState} onChange={e => setLakeState(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} value={lakeDescription} onChange={e => setLakeDescription(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Hero Image URL</Form.Label>
              <Form.Control type="url" value={lakeHero} onChange={e => setLakeHero(e.target.value)} placeholder="https://..." />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLakeModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={actionLoading}>
              {actionLoading ? <Spinner size="sm" /> : 'Save Lake'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add Tournament Modal */}
      <Modal show={showTourneyModal} onHide={() => setShowTourneyModal(false)}>
        <Form onSubmit={handleTourneySubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add Tournament</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Target Lake <span className="text-danger">*</span></Form.Label>
              <Form.Select value={tourneyLakeId} onChange={e => setTourneyLakeId(e.target.value)} required>
                <option value="">Select a lake...</option>
                {lakes.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tournament Title <span className="text-danger">*</span></Form.Label>
              <Form.Control type="text" value={tourneyTitle} onChange={e => setTourneyTitle(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Organizer</Form.Label>
              <Form.Control type="text" value={tourneyOrganizer} onChange={e => setTourneyOrganizer(e.target.value)} />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control type="date" value={tourneyStart} onChange={e => setTourneyStart(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control type="date" value={tourneyEnd} onChange={e => setTourneyEnd(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Registration URL</Form.Label>
              <Form.Control type="url" value={tourneyRegUrl} onChange={e => setTourneyRegUrl(e.target.value)} placeholder="https://..." />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTourneyModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={actionLoading}>
              {actionLoading ? <Spinner size="sm" /> : 'Save Tournament'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
